import React from "react";
import { motion } from "framer-motion";
import { VaultLogo } from "@/components/VaultLogo";
import { ArrowDown } from "lucide-react";

const Hero = () => {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen w-full bg-[#080808] overflow-hidden flex items-center justify-center pt-24 pb-16"
    >
      {/* Decorative globe arcs background */}
      <svg
        className="absolute inset-0 w-full h-full opacity-[0.06] pointer-events-none"
        viewBox="0 0 1440 900"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <ellipse cx="720" cy="450" rx="700" ry="260" stroke="#C9960C" strokeWidth="1" />
        <ellipse cx="720" cy="450" rx="560" ry="380" stroke="#C9960C" strokeWidth="1" />
        <ellipse cx="720" cy="450" rx="400" ry="460" stroke="#C9960C" strokeWidth="1" />
        <line x1="20" y1="450" x2="1420" y2="450" stroke="#C9960C" strokeWidth="0.5" />
        <line x1="720" y1="40" x2="720" y2="860" stroke="#C9960C" strokeWidth="0.5" />
      </svg>

      {/* Corner ornaments */}
      <div className="absolute top-28 left-6 sm:left-10 hidden md:block">
        <div className="h-10 w-px bg-[#C9960C]/40" />
        <div className="mt-2 text-[10px] tracking-[0.4em] uppercase text-[#C9960C]/60 -rotate-90 origin-top-left translate-y-6">
          Est. Globally
        </div>
      </div>
      <div className="absolute top-28 right-6 sm:right-10 hidden md:block text-right">
        <div className="text-[10px] tracking-[0.4em] uppercase text-[#C9960C]/60">Private &amp; Confidential</div>
        <div className="mt-2 h-10 w-px bg-[#C9960C]/40 ml-auto" />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="flex justify-center mb-10"
        >
          <VaultLogo size={210} />
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="font-serif-display text-[#F5D485] font-bold leading-[0.95]"
        >
          <span
            data-testid="hero-the"
            className="block text-5xl sm:text-7xl lg:text-8xl tracking-tight"
          >
            THE
          </span>
          <span
            data-testid="hero-global-vault"
            className="block text-5xl sm:text-7xl lg:text-8xl tracking-tight mt-1"
          >
            GLOBAL VAULT
          </span>
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, scaleX: 0 }}
          animate={{ opacity: 1, scaleX: 1 }}
          transition={{ duration: 1.2, delay: 0.7 }}
          className="mx-auto mt-8 mb-6 h-px bg-[#C9960C] origin-center pulse-line"
          style={{ width: 180 }}
        />

        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9 }}
          data-testid="hero-tagline"
          className="italic text-[#F5D485] tracking-[0.45em] text-sm sm:text-base uppercase font-light"
        >
          Wealth&nbsp;&nbsp;Without&nbsp;&nbsp;Borders
        </motion.p>

        <motion.h2
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1 }}
          data-testid="hero-subheading"
          className="font-serif-display mt-10 text-2xl sm:text-3xl lg:text-4xl text-[#F8F6F0]/90 max-w-3xl mx-auto leading-snug"
        >
          Deploying Indian Wealth Globally
        </motion.h2>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.3 }}
          className="mt-12 flex flex-col sm:flex-row gap-4 sm:gap-6 items-center justify-center"
        >
          <button
            onClick={() => scrollTo("how-it-works")}
            data-testid="hero-cta-btn"
            className="inline-flex items-center justify-center gap-2 bg-[#080808] border border-[#C9960C] text-[#C9960C] px-9 py-4 uppercase tracking-[0.2em] text-xs sm:text-sm font-medium hover:bg-[#C9960C] hover:text-[#080808] transition-colors duration-300 cursor-pointer"
          >
            Explore How It Works
            <ArrowDown size={16} className="ml-1" />
          </button>
          <button
            onClick={() => scrollTo("contact")}
            data-testid="hero-secondary-btn"
            className="text-[#F5D485]/80 hover:text-[#C9960C] uppercase tracking-[0.2em] text-xs sm:text-sm border-b border-[#F5D485]/40 hover:border-[#C9960C] pb-1 transition-colors cursor-pointer"
          >
            Speak Privately
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
